import { Fragment, useEffect, useRef, useState } from 'react'
import { PickList, TextField } from '@bloo-team/ui'
import { QueryHighlight } from '@bloo-team/core-shell'
import { SIGNAL_ENTITY_KEYS } from '@bloo-team/api-client'
import { useAttackTactics } from '../../../lib/coverage'
import { useSignalTags } from '../../../lib/signals'
import {
  applySignalSpec, defaultSubtechnique, readSignalSpec, severityForScore, signalSpecComplete, useSubtechniques,
  useTechniquesForTactic,
} from '../../../lib/workbook-blocks'
import type { SignalEntity, SignalSpec } from '../../../lib/workbook-blocks'
import { InlineKv, Labeled, Section, SeverityUnit } from './parts'
import { SignalTagCreate, TagChip } from './SignalTags'
import type { BlockEditorProps, BlockViewProps } from './types'

/**
 * Signal: turns the previous block's rows into signals. Editable: name, score
 * (severity derives), MITRE tactic / technique / sub-technique, tags, entity
 * mappings from the fixed twelve keys, the raw event query and the KB note.
 * Confidence is shown read-only; source streams, Workbook and is_generated are
 * not shown. All of them pass through unchanged (applySignalSpec).
 */

const SCORES = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
const keyOf = (e: SignalEntity) => `${e.role}${e.field}`
const entityOf = (key: string, value: string): SignalEntity =>
  key.startsWith('Target')
    ? { role: 'Target', field: key.slice(6), value }
    : { role: 'Suspect', field: key.slice(7), value }

/** A tag's color from the scope's signal tags ('' when unknown or uncolored). */
function useTagColor(clusterId: string, scopeId: string) {
  const tagsQ = useSignalTags({ clusterId, scopeId })
  return { tagsQ, colorOf: (value: string) => tagsQ.data?.find((t) => t.value === value)?.color ?? '' }
}

export function SignalView({ block, clusterId, scopeId }: BlockViewProps) {
  const spec = readSignalSpec(block)
  const { colorOf } = useTagColor(clusterId, scopeId)
  const mitre = [spec.tactic, spec.technique, spec.subtechnique].filter(Boolean)
  return (
    <div className="wb-secs">
      <div className="wb-sec">
        <InlineKv
          className="wb-signal-lead"
          items={[
            ['severity', <SeverityUnit key="sev" severity={severityForScore(spec.score)} score={spec.score} />],
            ['confidence', spec.confidence],
            ['detection name', spec.name],
          ]}
        />
      </div>
      <Section label="MITRE ATT&CK">
        {mitre.length ? (
          <div className="wb-mitre">
            {mitre.map((m, i) => (
              <Fragment key={m}>
                {i > 0 ? <span className="sep" aria-hidden="true">›</span> : null}
                <span className={i === 2 ? 'mono' : undefined}>{m}</span>
              </Fragment>
            ))}
          </div>
        ) : (
          <p className="wb-empty">untagged</p>
        )}
      </Section>
      <Section label="Entity mapping" count={spec.entities.length}>
        {spec.entities.length ? (
          <ul className="sp-entities">
            {spec.entities.map((e) => (
              <li key={keyOf(e)} className="sp-entity" data-role={e.role}>
                <span className="sp-entity-role">{e.role}</span>
                <span className="sp-entity-field">{e.field}</span>
                <span className="sp-entity-value">{e.value}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="wb-empty">none</p>
        )}
      </Section>
      {/* Tags, raw event query and KB note only when they have content. */}
      {spec.tags.length ? (
        <Section label="Tags">
          <div className="wb-chips">{spec.tags.map((t) => <TagChip key={t} value={t} color={colorOf(t)} />)}</div>
        </Section>
      ) : null}
      {spec.rawEventQuery ? (
        <Section label="Raw event query">
          <QueryHighlight className="wb-well" value={spec.rawEventQuery} dialect="dql" />
        </Section>
      ) : null}
      {spec.kb ? (
        <Section label="KB note">
          <details className="wb-kb">
            <summary>show</summary>
            <p className="hud-unit wb-prewrap">{spec.kb}</p>
          </details>
        </Section>
      ) : null}
    </div>
  )
}

export function SignalEditor({ block, clusterId, scopeId, index, onChange, prevFields }: BlockEditorProps) {
  const spec = readSignalSpec(block)
  const tacticsQ = useAttackTactics({ clusterId, scopeId })
  const techniquesQ = useTechniquesForTactic({ clusterId, scopeId, tactic: spec.tactic || null })
  const subtechQ = useSubtechniques({ clusterId, scopeId, techniqueName: spec.technique || null })
  const set = (patch: Partial<SignalSpec>) => onChange(applySignalSpec(block, { ...spec, ...patch }))
  const complete = signalSpecComplete(spec)
  // Tags come from the scope's signal tags (signals/tag/list); tags already on the
  // block stay selectable even when the list no longer has them. The picker's
  // create row opens SignalTagCreate, anchored under the picker, with the typed text.
  const { tagsQ, colorOf } = useTagColor(clusterId, scopeId)
  const tagOptions = [...new Set([...(tagsQ.data ?? []).filter((t) => t.enabled).map((t) => t.value), ...spec.tags])]
  const tagAnchor = useRef<HTMLDivElement>(null)
  const [creatingTag, setCreatingTag] = useState<string | null>(null)
  /**
   * A technique the user just picked: once its options load, the sub-technique
   * defaults to the technique itself ("T1592 - Gather Victim Host Information").
   * Only on a user change — opening a block never rewrites its sub-technique.
   */
  const defaultFor = useRef<string | null>(null)
  useEffect(() => {
    if (defaultFor.current === null || defaultFor.current !== spec.technique || !subtechQ.data) return
    defaultFor.current = null
    const d = defaultSubtechnique(subtechQ.data)
    if (d && !spec.subtechnique) set({ subtechnique: d })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec.technique, spec.subtechnique, subtechQ.data])
  /**
   * Rows added but not yet given a result field. applySignalSpec (rightly)
   * never writes an empty mapping, so a new row lives here until it has a value.
   */
  const [pending, setPending] = useState<string[]>([])
  const used = new Set([...spec.entities.map(keyOf), ...pending])
  const free = SIGNAL_ENTITY_KEYS.filter((k) => !used.has(k))
  const rows: Array<{ entity: SignalEntity; pending: boolean }> = [
    ...spec.entities.map((entity) => ({ entity, pending: false })),
    ...pending.filter((k) => !spec.entities.some((e) => keyOf(e) === k)).map((k) => ({ entity: entityOf(k, ''), pending: true })),
  ]
  const setRow = (i: number, next: SignalEntity) => {
    const row = rows[i]
    if (!row) return
    if (row.pending) {
      setPending((p) => p.filter((k) => k !== keyOf(row.entity)).concat(next.value.trim() ? [] : [keyOf(next)]))
      if (next.value.trim()) set({ entities: [...spec.entities, next] })
      return
    }
    if (!next.value.trim()) {
      // Cleared: keep the row on screen (pending) rather than let it vanish mid-edit.
      setPending((p) => [...p, keyOf(next)])
      set({ entities: spec.entities.filter((x) => keyOf(x) !== keyOf(row.entity)) })
      return
    }
    set({ entities: spec.entities.map((x) => (keyOf(x) === keyOf(row.entity) ? next : x)) })
  }
  const removeRow = (i: number) => {
    const row = rows[i]
    if (!row) return
    if (row.pending) setPending((p) => p.filter((k) => k !== keyOf(row.entity)))
    else set({ entities: spec.entities.filter((x) => keyOf(x) !== keyOf(row.entity)) })
  }

  return (
    <div className="wb-secs">
      <Section label="Identity">
        <div className="wb-fields">
          <Labeled label="score">
            <PickList
              label="Detection score"
              placeholder="score…"
              options={SCORES}
              selected={spec.score ? [spec.score] : []}
              onChange={(next) => set({ score: next[0] ?? '' })}
            />
          </Labeled>
          <span className="wb-sev-edit"><SeverityUnit severity={severityForScore(spec.score)} /></span>
          <TextField className="wb-w-xs" label="confidence" value={spec.confidence} isReadOnly />
          <TextField className="wb-w-lg" label="detection name" value={spec.name} onChange={(v) => set({ name: v })} />
        </div>
      </Section>
      <Section label="MITRE ATT&CK">
        <div className="wb-fields">
          <PickList
            label="MITRE tactic"
            placeholder="tactic…"
            options={(tacticsQ.data ?? []).filter((t) => t.toLowerCase() !== 'unclassified')}
            selected={spec.tactic ? [spec.tactic] : []}
            onChange={(next) => {
              defaultFor.current = null
              set({ tactic: next[0] ?? '', technique: '', subtechnique: '' })
            }}
          />
          <PickList
            label="MITRE technique"
            placeholder="technique…"
            options={techniquesQ.data ?? []}
            selected={spec.technique ? [spec.technique] : []}
            disabled={!spec.tactic}
            onChange={(next) => {
              const technique = next[0] ?? ''
              defaultFor.current = technique || null
              set({ technique, subtechnique: '' })
            }}
          />
          <PickList
            label="MITRE sub-technique"
            placeholder="sub-technique…"
            options={subtechQ.data ?? []}
            selected={spec.subtechnique ? [spec.subtechnique] : []}
            disabled={!spec.technique || (subtechQ.data ?? []).length === 0}
            onChange={(next) => set({ subtechnique: next[0] ?? '' })}
          />
        </div>
      </Section>
      <Section label="Entity mapping · ≥ 1 required">
        <div className="hud-stack wb-gap-1">
          {rows.map(({ entity: e }, i) => (
            <div key={keyOf(e)} className="wb-ent-row">
              <PickList
                label={`Entity ${i + 1}`}
                options={[keyOf(e), ...free]}
                selected={[keyOf(e)]}
                onChange={(next) => {
                  const k = next[0]
                  if (k) setRow(i, entityOf(k, e.value))
                }}
              />
              <span className="eq" aria-hidden="true">=</span>
              <TextField
                className="wb-w-md"
                aria-label={`Entity ${i + 1} result field`}
                placeholder={prevFields[0] ? `e.g. ${prevFields[0]}` : 'result field'}
                value={e.value}
                onChange={(v) => setRow(i, { ...e, value: v })}
              />
              <button type="button" className="row-action" aria-label={`Remove entity ${i + 1}`} onClick={() => removeRow(i)}>
                ✕
              </button>
            </div>
          ))}
          <div>
            <button
              type="button"
              className="list-chip"
              disabled={free.length === 0}
              onClick={() => {
                const k = free[0]
                if (k) setPending((p) => [...p, k])
              }}
            >
              + entity
            </button>
          </div>
        </div>
      </Section>
      <Section label="Tags">
        <div className="hud-stack wb-gap-1">
          <div className="wb-fields" ref={tagAnchor}>
            <PickList
              label={`Block ${index + 1} tags`}
              multi
              placeholder={tagsQ.isPending ? 'loading tags…' : tagOptions.length ? 'tags…' : 'no tags yet · type to create'}
              options={tagOptions}
              selected={spec.tags}
              disabled={tagsQ.isPending}
              onChange={(tags) => set({ tags })}
              onCreate={setCreatingTag}
              createLabel={(q) => `Create tag "${q}"`}
              optionColor={colorOf}
            />
          </div>
          {spec.tags.length ? (
            <ul className="wb-chips wb-chip-list" aria-label={`Block ${index + 1} selected tags`}>
              {spec.tags.map((t) => (
                <li key={t}>
                  <TagChip value={t} color={colorOf(t)} onRemove={() => set({ tags: spec.tags.filter((x) => x !== t) })} />
                </li>
              ))}
            </ul>
          ) : null}
          {creatingTag !== null ? (
            <SignalTagCreate
              triggerRef={tagAnchor}
              initialName={creatingTag}
              clusterId={clusterId}
              scopeId={scopeId}
              onClose={() => setCreatingTag(null)}
            />
          ) : null}
        </div>
      </Section>
      <Section label="Raw event query">
        <label className="bui-field wb-cap">
          <textarea
            className="hunt-editor mono"
            aria-label={`Block ${index + 1} raw event query`}
            rows={3}
            spellCheck={false}
            placeholder="stream=… where srcip='{SuspectHost}' | duration 30d"
            value={spec.rawEventQuery ?? ''}
            onChange={(ev) => set({ rawEventQuery: ev.target.value })}
          />
          <span className="bui-hint">{'{Entity} placeholders are filled from the mapped entities'}</span>
        </label>
      </Section>
      <Section label="KB note">
        <label className="bui-field wb-cap">
          <textarea
            className="hunt-editor"
            aria-label={`Block ${index + 1} detection KB`}
            rows={3}
            value={spec.kb}
            onChange={(ev) => set({ kb: ev.target.value })}
          />
        </label>
      </Section>

      {!complete ? (
        <div className="bui-alert bui-alert-warning" role="alert">
          <span className="bui-alert-mark">!</span>
          <span>
            Signal block incomplete: name, score, tactic, technique and at least one entity mapping are
            required before this workbook can save.
          </span>
        </div>
      ) : null}
    </div>
  )
}
